import React, {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';

export interface DrawCanvasHandle {
    toDataURL: () => string;
    clear: () => void;
}

/** Simple freehand canvas for finger/mouse drawing - no dependency, single-pointer only. */
const DrawCanvas = forwardRef<DrawCanvasHandle>((_, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const lastPoint = useRef<{x: number; y: number} | null>(null);

    const fillWhite = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.scale(dpr, dpr);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = '#14141a';
            ctx.lineWidth = 4;
        }
        fillWhite();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = canvasRef.current!.getBoundingClientRect();
        return {x: e.clientX - rect.left, y: e.clientY - rect.top};
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drawingRef.current = true;
        lastPoint.current = getPos(e);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawingRef.current || !lastPoint.current) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const point = getPos(e);
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
        lastPoint.current = point;
    };

    const stopDrawing = () => {
        drawingRef.current = false;
        lastPoint.current = null;
    };

    useImperativeHandle(ref, () => ({
        toDataURL: () => canvasRef.current?.toDataURL('image/png') || '',
        clear: fillWhite,
    }));

    return (
        <canvas
            ref={canvasRef}
            className="GmDrawCanvas"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDrawing}
            onPointerLeave={stopDrawing}
            onPointerCancel={stopDrawing}
        />
    );
});

export default DrawCanvas;
